import {RefSelector} from '@github-ui/ref-selector'
import {ReposSelector} from '@github-ui/repos-selector'
import {Button, FormControl, Banner, IconButton, VisuallyHidden} from '@primer/react'
import {InlineMessage} from '@primer/react/experimental'
import {useCallback, useRef, useState, useImperativeHandle, useMemo, useId, useEffect} from 'react'
import {GitBranchIcon, GitBranchCheckIcon} from '@primer/octicons-react'
import {IconButtonSelector} from '@github-ui/copilot-chat/components/IconButtonSelector'
import {useEntitlement} from '@github-ui/copilot-chat/components/quota/EntitlementContext'
import {CopilotPlan} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {
  COPILOT_ASSIGNMENT_PROMPT_PLACEHOLDER,
  COPILOT_INSTRUCTIONS_MAX_LENGTH,
  COPILOT_ASSIGNMENT_BRANCH_CAPTION,
  COPILOT_ASSIGNMENT_REPO_LABEL,
  COPILOT_ASSIGNMENT_BRANCH_LABEL,
  COPILOT_ASSIGNMENT_AGENT_LABEL,
  COPILOT_ASSIGNMENT_PROMPT_LABEL,
  COPILOT_ASSIGNMENT_SELECT_BRANCH_HEADING,
  AGENT_ASSIGNMENT_PROMPT_PLACEHOLDER,
  AGENT_ASSIGNMENT_BRANCH_CAPTION,
} from '../../utils/constants'
import {
  useCopilotRepositoryFetching,
  type DropdownRepository,
  type SimpleRepository,
} from '../../hooks/useCopilotRepoFetching'
import {useDefaultOwnerAwareRepositoryLoader} from '../../hooks/useDefaultOwnerAwareRepositoryLoader'
import type {FormCustomizations} from './copilot-assignment-types'
import styles from './CopilotAssignmentForm.module.css'
import {CommandButton} from '@github-ui/ui-commands'
import {AgentSelector} from '../new-task-chat-input/AgentSelector'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ModelPicker} from '../task-view/ModelPicker'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import type {SelectedAgent} from '../../types/session'
import {agentSupportsModelSelection} from '../../utils/agent-helpers'
import {useLastSelectedAgent} from '../../hooks/use-last-selected-agent'
import {useAgentVendorModel} from '../../hooks/use-agent-vendor-model'
import {useTopLevelAgents} from '../../hooks/useTopLevelAgents'
import type {AgentQuotaSnapshot} from '../../types/agent-quota-snapshot'
import {QuotaBanner} from '../task-view/QuotaBanner'

const CROSS_ORG_WARNING_TEXT_AGENT =
  "You're assigning the agent to perform work in a different organization. The issue description will be visible to users outside this repository."
const PRIVATE_TO_PUBLIC_WARNING_TEXT_AGENT =
  "You're assigning the agent to perform work in a public repository. The issue description will be visible to users outside this repository."
const EMPTY_REPO_WARNING_TEXT =
  "The selected repo has no commits or branches so there's no valid base branch to create a PR against."

export interface CopilotAssignmentFormRef {
  submit: () => Promise<void>
}

export interface CopilotAssignmentFormProps {
  initialRepo: SimpleRepository
  onSubmit: (
    content: string,
    selectedRepo: DropdownRepository | null,
    selectedBranch: string,
    selectedAgent: SelectedAgent,
    selectedModel: string | undefined,
  ) => Promise<void>
  onCancel?: () => void
  isSubmitting?: boolean
  textareaRef?: React.RefObject<HTMLTextAreaElement | null>
  textareaAriaDescribedBy?: string
  nested?: boolean
  ref?: React.Ref<CopilotAssignmentFormRef>
  initialAgent?: SelectedAgent
  onAgentChange?: (agent: SelectedAgent) => void
  initialFormCustomizations?: FormCustomizations
  onCustomizationsChange?: (customizations: FormCustomizations) => void
  agentQuotaSnapshot?: AgentQuotaSnapshot | null
}

export function CopilotAssignmentForm({
  ref,
  initialRepo,
  onSubmit,
  onCancel,
  isSubmitting = false,
  textareaRef,
  textareaAriaDescribedBy,
  initialAgent,
  onAgentChange,
  initialFormCustomizations,
  onCustomizationsChange,
  agentQuotaSnapshot,
}: CopilotAssignmentFormProps) {
  const {plan, tokenBasedBillingEnabled: isTokenBasedBillingEnabled} = useEntitlement()
  // Pre-fill prompt from previous modal's customizations if available
  const [content, setContent] = useState(initialFormCustomizations?.content ?? '')
  const [textareaValidationError, setTextareaValidationError] = useState<string | undefined>(undefined)
  const [branchValidationError, setBranchValidationError] = useState<string | undefined>(undefined)

  const [selectedAgent, setSelectedAgent] = useLastSelectedAgent(initialAgent)

  // Only fetch the top-level agents list when the selected agent needs enrichment
  // (missing modelVendors). This avoids unnecessary requests in tests and when
  // the agent was selected from AgentListItems (which already sets modelVendors).
  const needsVendorEnrichment = selectedAgent.typeName === 'topLevel' && !selectedAgent.modelVendors
  const initialRepoNameWithOwner = `${initialRepo.owner.login}/${initialRepo.name}`
  const {data: topLevelAgents = []} = useTopLevelAgents(needsVendorEnrichment ? initialRepoNameWithOwner : undefined)

  // Enrich selectedAgent with modelVendors from the agents list when it's missing
  // (e.g., when coming from the issue assignee picker which doesn't have vendor info).
  const enrichedAgent = useMemo(() => {
    if (needsVendorEnrichment && topLevelAgents.length > 0) {
      const matched = topLevelAgents.find(
        a => a.bot_global_relay_id === selectedAgent.botGlobalRelayId || a.slug === selectedAgent.login,
      )
      if (matched?.model_vendors && matched.model_vendors.length > 0) {
        return {...selectedAgent, modelVendors: matched.model_vendors}
      }
    }
    return selectedAgent
  }, [selectedAgent, topLevelAgents, needsVendorEnrichment])

  const {
    agentSlug,
    vendorFilters,
    selectedModelId: selectedModel,
    setSelectedModelId: setSelectedModel,
  } = useAgentVendorModel(enrichedAgent)

  // Generate a unique cache key per modal mount to prevent stale branch data
  const cacheKey = `copilot-assignment-ref-selector-${useId()}`

  const internalTextareaRef = useRef<HTMLTextAreaElement>(null)
  const finalTextareaRef = textareaRef || internalTextareaRef

  // Create defaultOwner-aware repository loader that ensures initial repo is always fetched
  const defaultOwnerAwareRepositoryLoader = useDefaultOwnerAwareRepositoryLoader(
    initialRepo.owner.login,
    initialRepoNameWithOwner,
  )

  // Build repo/branch overrides from previous modal's customizations
  const repoOverrides = useMemo(
    () =>
      initialFormCustomizations?.selectedRepo
        ? {
            selectedRepo: initialFormCustomizations.selectedRepo,
            selectedBranch: initialFormCustomizations.selectedBranch,
          }
        : undefined,
    [initialFormCustomizations],
  )

  const {
    selectedRepo,
    selectedBranch,
    setSelectedBranch,
    isLoadingBranch,
    initialRepoApiMetadata,
    handleRepoSelect: handleRepoSelectFromHook,
    handleReposLoaded,
  } = useCopilotRepositoryFetching(initialRepo, repoOverrides)

  const isSweSubagentsEnabled = isFeatureEnabled('copilot_swe_agent_use_subagents')
  const useAgentLabels = isFeatureEnabled('issue_cca_modal_open')
  const isBlockedByQuota = agentQuotaSnapshot?.can_start_session === false
  const quotaBlockedDescriptionId = 'copilot-assignment-quota-blocked-message'

  const warningMessage = useMemo(() => {
    if (!selectedRepo) return null

    const copilotAgentName = 'Copilot cloud agent'
    const targetIsPublic = !selectedRepo.private
    const differentOrganization = initialRepo.owner.login !== selectedRepo.owner

    if (selectedRepo.isEmpty) {
      return EMPTY_REPO_WARNING_TEXT
    }

    if (differentOrganization) {
      return useAgentLabels
        ? CROSS_ORG_WARNING_TEXT_AGENT
        : `You're assigning ${copilotAgentName} to perform work in a different organization. The issue description will be visible to users outside this repository.`
    }

    if (initialRepoApiMetadata?.private && targetIsPublic) {
      return useAgentLabels
        ? PRIVATE_TO_PUBLIC_WARNING_TEXT_AGENT
        : `You're assigning ${copilotAgentName} to perform work in a public repository. The issue description will be visible to users outside this repository.`
    }

    return null
  }, [selectedRepo, initialRepo.owner.login, initialRepoApiMetadata, useAgentLabels])

  // Wrapper to prevent selection during submission
  const handleRepoSelect = useCallback(
    (repo: DropdownRepository | undefined) => {
      if (!repo || isSubmitting) return
      handleRepoSelectFromHook(repo)
      setBranchValidationError(undefined)
    },
    [isSubmitting, handleRepoSelectFromHook],
  )

  const handleSubmit = useCallback(async () => {
    if (isSubmitting || isBlockedByQuota) return

    setTextareaValidationError(undefined)
    setBranchValidationError(undefined)

    // Validate character limit
    if (content.length > COPILOT_INSTRUCTIONS_MAX_LENGTH) {
      const errorMessage = `Instructions must be ${COPILOT_INSTRUCTIONS_MAX_LENGTH.toLocaleString()} characters or fewer. Current length: ${content.length.toLocaleString()} characters.`
      setTextareaValidationError(errorMessage)
      return
    }

    if (!selectedRepo || !selectedBranch) {
      setBranchValidationError('Please select a repository and branch.')
      return
    }

    if (selectedRepo.isEmpty) {
      setBranchValidationError('Please select a valid base branch.')
      return
    }

    await onSubmit(content, selectedRepo, selectedBranch, selectedAgent, selectedModel)
  }, [content, selectedRepo, selectedBranch, selectedAgent, selectedModel, onSubmit, isSubmitting, isBlockedByQuota])

  // Expose submit method through ref
  useImperativeHandle(
    ref,
    () => ({
      submit: handleSubmit,
    }),
    [handleSubmit],
  )

  // Report current form customizations to parent whenever they change
  useEffect(() => {
    onCustomizationsChange?.({content, selectedRepo, selectedBranch})
  }, [content, selectedRepo, selectedBranch, onCustomizationsChange])

  const submitButtonInactive =
    isBlockedByQuota || selectedRepo?.isEmpty || isSubmitting || !selectedRepo || !selectedBranch
  const refSelectorInactive = isSubmitting || isLoadingBranch || !selectedRepo?.defaultBranch || selectedRepo?.isEmpty
  const customAgentPickerInactive = isSubmitting
  const ariaDescribedBy =
    [
      textareaAriaDescribedBy,
      textareaValidationError ? 'copilot-assignment-prompt-error' : undefined,
      isBlockedByQuota ? quotaBlockedDescriptionId : undefined,
    ]
      .filter(Boolean)
      .join(' ') || undefined

  return (
    <div className={styles.formContainer}>
      {/* Label outside the input container */}
      <label htmlFor="copilot-assignment-prompt" className={styles.promptLabel}>
        {COPILOT_ASSIGNMENT_PROMPT_LABEL}
      </label>

      {/* Input container with embedded toolbar - matches ChatInput styling */}
      <div className={styles.inputContainer}>
        <div className={styles.textareaContainer}>
          <textarea
            id="copilot-assignment-prompt"
            ref={finalTextareaRef}
            className={styles.textarea}
            placeholder={useAgentLabels ? AGENT_ASSIGNMENT_PROMPT_PLACEHOLDER : COPILOT_ASSIGNMENT_PROMPT_PLACEHOLDER}
            value={content}
            onChange={e => setContent(e.target.value)}
            rows={2}
            aria-describedby={ariaDescribedBy}
            maxLength={COPILOT_INSTRUCTIONS_MAX_LENGTH}
            disabled={isSubmitting || isBlockedByQuota}
          />
          {textareaValidationError && (
            <span id="copilot-assignment-prompt-error" className={styles.validationError}>
              {textareaValidationError}
            </span>
          )}
        </div>

        {/* Toolbar embedded inside the input container */}
        <div className={styles.toolbar}>
          <div className={styles.selectorsRow}>
            <FormControl className={styles.formControl}>
              <FormControl.Label visuallyHidden>{COPILOT_ASSIGNMENT_REPO_LABEL}</FormControl.Label>
              <ReposSelector
                repositoryLoader={defaultOwnerAwareRepositoryLoader}
                selectionVariant="single"
                selectAllOption={false}
                onSelect={handleRepoSelect}
                onLoad={handleReposLoaded}
                currentSelection={selectedRepo || undefined}
                buttonText={selectedRepo?.nameWithOwner || 'Select repository'}
                width="large"
                truncateMaxWidth={250}
                additionalButtonProps={{
                  variant: 'invisible',
                  className: styles.toolbarButton,
                  'aria-label': `Repository: ${selectedRepo?.nameWithOwner || 'Select repository'}`,
                  disabled: isSubmitting,
                }}
              />
            </FormControl>

            <FormControl className={styles.formControl}>
              <FormControl.Label visuallyHidden>{COPILOT_ASSIGNMENT_BRANCH_LABEL}</FormControl.Label>
              {selectedRepo || selectedBranch ? (
                <RefSelector
                  useFocusZone
                  cacheKey={cacheKey}
                  canCreate={false}
                  currentCommitish={selectedBranch || selectedRepo?.defaultBranch || ''}
                  defaultBranch={selectedRepo?.defaultBranch ?? selectedBranch}
                  owner={selectedRepo?.owner || ''}
                  repo={selectedRepo?.nameOnly || ''}
                  types={['branch']}
                  selectedRefType="branch"
                  closeOnSelect
                  hideShowAll
                  disabled={refSelectorInactive}
                  actionListHeadingText={COPILOT_ASSIGNMENT_SELECT_BRANCH_HEADING}
                  actionListDescription={
                    useAgentLabels ? AGENT_ASSIGNMENT_BRANCH_CAPTION : COPILOT_ASSIGNMENT_BRANCH_CAPTION
                  }
                  onSelectItem={selectedRef => {
                    if (!isSubmitting) {
                      setSelectedBranch(selectedRef)
                    }
                  }}
                  renderAnchor={props => (
                    <IconButtonSelector
                      {...props}
                      variant="invisible"
                      className={styles.toolbarButton}
                      icon={GitBranchIcon}
                      selectedIcon={GitBranchCheckIcon}
                      selected={selectedBranch !== selectedRepo?.defaultBranch}
                      aria-label={
                        selectedRepo?.isEmpty
                          ? 'Branch: None available'
                          : `Branch: ${isLoadingBranch ? 'Loading branch...' : selectedBranch || 'Select branch'}`
                      }
                      aria-labelledby={undefined}
                    />
                  )}
                  anchoredOverlayProps={{
                    variant: {regular: 'anchored', narrow: 'fullscreen'},
                    height: 'small',
                  }}
                />
              ) : (
                <IconButton
                  variant="invisible"
                  className={styles.toolbarButton}
                  icon={GitBranchIcon}
                  aria-label="Loading branches…"
                  disabled
                />
              )}
            </FormControl>
            {(isSweSubagentsEnabled || useAgentLabels) && selectedRepo && (
              <FormControl className={styles.formControl}>
                <FormControl.Label visuallyHidden>{COPILOT_ASSIGNMENT_AGENT_LABEL}</FormControl.Label>
                <AgentSelector
                  repo={selectedRepo}
                  branch={selectedBranch || selectedRepo.defaultBranch}
                  selectedAgent={selectedAgent}
                  onSelectAgent={agent => {
                    setSelectedAgent(agent)
                    onAgentChange?.(agent)
                  }}
                  iconOnly
                  disabled={customAgentPickerInactive}
                  buttonProps={{variant: 'invisible', className: styles.toolbarButton}}
                />
              </FormControl>
            )}
            {copilotFeatureFlags.codingAgentModelSelection &&
              (copilotFeatureFlags.codingAgentModelSelectionAllSkus ||
                plan === CopilotPlan.IndividualPro ||
                plan === CopilotPlan.IndividualProPlus ||
                plan === CopilotPlan.IndividualMax ||
                plan === CopilotPlan.IndividualStudent) &&
              agentSupportsModelSelection(selectedAgent, agentSlug) && (
                <div className={styles.modelPickerContainer}>
                  <FormControl>
                    <FormControl.Label visuallyHidden>Model</FormControl.Label>
                    <ModelPicker
                      onUpdateModel={setSelectedModel}
                      selectedModelId={selectedModel}
                      vendorFilters={vendorFilters}
                      isTokenBasedBillingEnabled={isTokenBasedBillingEnabled}
                    />
                  </FormControl>
                </div>
              )}
          </div>
        </div>
      </div>

      {warningMessage && (
        <Banner variant="warning" title="Warning" hideTitle className={styles.warningBanner}>
          {warningMessage}
        </Banner>
      )}

      {branchValidationError && (
        <InlineMessage variant="critical" className={styles.branchValidation}>
          {branchValidationError}
        </InlineMessage>
      )}

      <QuotaBanner agentQuotaSnapshot={agentQuotaSnapshot} />
      <div className={styles.buttonsContainer}>
        <div className={styles.buttonsRow}>
          {onCancel && (
            <Button variant="invisible" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
          )}
          <CommandButton
            commandId="github:submit-form"
            showKeybindingHint
            variant="primary"
            inactive={submitButtonInactive}
            loading={isSubmitting}
            className={styles.assignButton}
            aria-describedby={isBlockedByQuota ? quotaBlockedDescriptionId : undefined}
          >
            Assign
          </CommandButton>
        </div>
      </div>

      {isBlockedByQuota && (
        <VisuallyHidden id={quotaBlockedDescriptionId}>
          Insufficient quota. Assignment is temporarily unavailable until additional usage is available.
        </VisuallyHidden>
      )}
    </div>
  )
}
